import { useEffect, Fragment, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  Typography,
} from "@material-ui/core";
import VerifyEmailForm from "../../components/authentication/VerifyEmailForm";
import Logo from "../../components/general/Logo";
import toast from "react-hot-toast";
import Spinner from "../../components/general/Spinner";
import useURLQuery from "../../hooks/useURLQuery";
import { useVerifyEmail } from "../../mutations/auth";

const VerifyEmail = () => {
  
  // Mutations

  const { mutateAsync: mutateAutoVerifyEmail } = useVerifyEmail();

  // Hooks

  const urlQuery = useURLQuery();
  const email = urlQuery.get("email");
  const token = urlQuery.get("token");
  const navigate = useNavigate();
  const [errors, setErrors] = useState(null);
  const [isDoingAutoVerifyEmail, setIsDoingAutoVerifyEmail] = useState(
    Boolean(email && token)
  );

  // The token is single use: a second call (StrictMode re-runs effects) would fail.
  // Pending state is tracked locally because StrictMode's simulated unmount
  // detaches the mutation observer, so isPending would never update.
  const hasRequestedVerify = useRef(false);
  useEffect(() => {
    // if an email and token is provided in the url's query
    // then we auto-verify the email
    // otherwise we display the form
    if (!email || !token || hasRequestedVerify.current) return;
    hasRequestedVerify.current = true;
    mutateAutoVerifyEmail({ email, token })
      .then(() => {
        toast.success("Email verified");
        navigate({ to: "/app" });
      })
      .catch(() => {
        toast.error("Email verification failed");
        setErrors("Email verification failed");
      })
      .finally(() => setIsDoingAutoVerifyEmail(false));
  }, [mutateAutoVerifyEmail, email, navigate, token]);

  // Rendering

  // Display a loader when verifying the email

  if (isDoingAutoVerifyEmail) {
    return (
      <Fragment>
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
            left: 0,
            p: 3,
            position: "fixed",
            top: 0,
            width: "100%",
            zIndex: 2000,
          }}
        >
          <Logo size={50} to="/" />
          <Box sx={{ mt: 3 }}>
            <Typography color="textPrimary" variant="h5">
              Verifying email...
            </Typography>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Spinner />
          </Box>
        </Box>
      </Fragment>
    );
  }

  // if email is set (link from email)
  if (email && token && !isDoingAutoVerifyEmail && !errors) {
    return null;
  }

  // if no email is set (not coming from email link), we display the page with the form

  return (
    <Fragment>
      <Helmet>
        <title>Lean Ads | Verify Email</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="sm" sx={{ py: 10 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Logo size={50} to="/" />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 8,
            }}
          />
          <Card>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                p: 4,
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <div>
                  <Typography color="textPrimary" gutterBottom variant="h4">
                    Verify Email
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    Confirm sign up using the code we sent you by email {email}
                  </Typography>
                </div>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  mt: 3,
                }}
              >
                <VerifyEmailForm />
              </Box>
              <Divider sx={{ my: 3 }} />
              <Link
                color="textSecondary"
                component={RouterLink}
                to="/authentication/forgot-password"
                variant="body2"
              >
                I did not receive the code
              </Link>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Fragment>
  );
};

export default VerifyEmail;
